<script setup lang="ts">
import { computed, ref, watch } from 'vue'

import { useAdvertiserGroupsRequest, useGetAdLinkDataRequest } from '@/api'
import AdvertiserPicker from '@/components/ReturnAdForm/AdvertiserPicker/AdvertiserPicker.vue'
import ReturnAdFormAdvertiserSelector from '@/components/ReturnAdForm/ReturnAdFormTarget/ReturnAdFormAdvertiserSelector.vue'
import { validateUrl } from '@/lib/helpers'
import type { AdvertiserType } from '@/lib/types/advertiser.types'
import type { ReturnAdFormType } from '@/lib/types/return-ad.types'
import { BoxComponent, FlexComponent, InputComponent, SelectComponent, TitleComponent, TypographyComponent } from '@/ui-library'

const props = defineProps<{ isEditMode: boolean }>()

const { data: advertiserGroups, isLoading: isAdvertiserGroupsLoading } = useAdvertiserGroupsRequest(true)
const advertiserGroupsOptions = computed(() => advertiserGroups.value.map((item) => ({ label: item.name, value: item.id })))
const showAdvertiserSelector = ref(false)
const possibleAdvertisers = ref<AdvertiserType[]>()

const internalName = defineModel<ReturnAdFormType['internalName']>('internalName', { required: true })
const returnLink = defineModel<ReturnAdFormType['returnLink']>('returnLink', { required: true })
const advertiserGroup = defineModel<ReturnAdFormType['advertiserGroup']>('advertiserGroup', { required: true })
const advertiserList = defineModel<ReturnAdFormType['advertiserList']>('advertiserList', { required: true })
const categories = defineModel<ReturnAdFormType['categories']>('categories', { required: true })
const isAllAdvertisersSelected = defineModel<ReturnAdFormType['isAllAdvertisersSelected']>('isAllAdvertisersSelected', { required: true })
const advertiser = defineModel<ReturnAdFormType['advertiser']>('advertiser')

const returnAdUserId = computed(() => advertiserGroups.value.find((group) => group.id === advertiserGroup.value)?.returnAdUserId)

const isReturnLinkDirty = ref(false)
const { data: adLinkData, isLoading: isLinkDataLoading, getLinkData, error: linkConversionError } = useGetAdLinkDataRequest(returnAdUserId)
const handleAdUrlChange = async (url: Event) => {
  const input = url.target as HTMLInputElement
  if (input?.value && isReturnLinkDirty.value && validateUrl(input.value)) {
    await getLinkData(input.value)
    isReturnLinkDirty.value = false
  }
}

watch(
  () => advertiserGroup.value,
  () => {
    if (!props.isEditMode) {
      advertiserList.value = []
      returnLink.value = ''
    }
  },
)

watch(
  () => adLinkData?.value,
  (data) => {
    if (data === undefined) {
      return
    }
    if (!data.length) {
      showAdvertiserSelector.value = false
      possibleAdvertisers.value = undefined
      advertiser.value = undefined
      return
    }
    if (data.length === 1) {
      advertiser.value = data[0]
      showAdvertiserSelector.value = false
      possibleAdvertisers.value = undefined
    } else {
      showAdvertiserSelector.value = true
      possibleAdvertisers.value = data
    }
  },
  { immediate: true },
)
</script>

<template>
  <FlexComponent direction="column" :gap="24">
    <el-form-item prop="internalName">
      <InputComponent v-model="internalName" name="internalName">
        <template #header>
          <BoxComponent :mb="8">
            <TitleComponent title="Internal name" />
          </BoxComponent>
        </template>
      </InputComponent>
    </el-form-item>

    <SelectComponent
      v-if="advertiserGroup"
      v-model="advertiserGroup"
      title="Advertiser group"
      filterable
      name="advertiserGroup"
      :options="advertiserGroupsOptions"
      :disabled="isEditMode"
      :is-fetching="isAdvertiserGroupsLoading"
    />
    <el-form-item prop="returnLink" :error="linkConversionError">
      <InputComponent
        v-model="returnLink"
        :is-disabled="isLinkDataLoading || isEditMode"
        name="returnLink"
        placeholder="https://"
        @focusout="handleAdUrlChange"
        @change="() => (isReturnLinkDirty = true)"
      >
        <template #header>
          <BoxComponent :mb="8">
            <TitleComponent title="Return link">
              <template #tooltip>
                <BoxComponent :w="280">
                  <BoxComponent :mb="4">
                    <TypographyComponent variant="title-medium">Return link</TypographyComponent>
                  </BoxComponent>
                  <TypographyComponent variant="body-medium">
                    When people press the button on the ad, they will be automatically redirected to this link.
                    <br /><br />
                    Please enter the link with “https://” in front.
                  </TypographyComponent>
                </BoxComponent>
              </template>
            </TitleComponent>
          </BoxComponent>
        </template>
      </InputComponent>
    </el-form-item>
    <BoxComponent>
      <BoxComponent :mb="8">
        <TitleComponent title="Display ad for advertisers" />
      </BoxComponent>

      <el-form-item prop="advertiserList">
        <ReturnAdFormAdvertiserSelector
          v-model:categories="categories"
          v-model:advertiser-list="advertiserList"
          v-model:is-all-advertisers-selected="isAllAdvertisersSelected"
          name="advertiserList"
          :advertiser-group="advertiserGroup"
          :is-edit-mode="isEditMode"
        />
      </el-form-item>
    </BoxComponent>
  </FlexComponent>
  <AdvertiserPicker
    v-if="possibleAdvertisers"
    v-model="showAdvertiserSelector"
    v-model:selected-advertiser="advertiser"
    :advertisers="possibleAdvertisers"
  />
</template>
